import type { ElectricalGroup, MembershipClearance, SubdivisionMember } from '../api';
import { clearanceCell, decisionClearance, formatDate, type RowTone } from '../sections/personnel-rows';

/**
 * Готовность работника в «Составе бригады» при выписке наряда — одним цветом
 * и одной строкой относительно выбранного вида работ.
 *
 * Прежде под каждым работником шла длинная строка через точки по всем
 * допускам, и главного — можно ли брать человека под этот вид работ — не было
 * видно (владелец, 27.09.2026). Телефон это уже показывает (`crewCandidate.ts`
 * в приложении), «Персонал» — цветом строки. Здесь то же в панели.
 *
 * ПРАВИЛ ЗДЕСЬ НЕТ. Что просрочено, что истекает и чего нет вовсе — решил
 * сервер тем же расчётом, что в «Персонале» (`clearances.logic.ts`); итог по
 * работнику — его же `overallStatus`. Группа сравнивается с той, что форма уже
 * показывает для вида работ. Окончательно решает выписка: полный расчёт по
 * матрице вида работ — на сервере, и его отказ показывается как есть.
 *
 * В строке — только то, что меняется: медосмотр, повторный инструктаж,
 * группа по электробезопасности, как в таблице «Персонала». Вводный и
 * первичный инструктаж разовые и одинаковые у всех — они в подробностях. Но
 * если такого инструктажа нет вовсе, это причина «Не допущен», и она названа.
 */

const GROUP_ORDER: readonly ElectricalGroup[] = ['II', 'III', 'IV', 'V'];

export interface CrewStatus {
  /** Тот же светофор, что строки «Персонала». */
  tone: Exclude<RowTone, 'inactive'>;
  /** «Готов · группа V» / «Не допущен: группа III, нужна V; медосмотр истёк 25.08.2026». */
  text: string;
  /** Причины отказа по отдельности — для подсказки у кнопки «Выписать наряд». */
  problems: string[];
}

/** Действующая группа работника; `null` — не подтверждена. */
export function crewGroup(member: Pick<SubdivisionMember, 'clearances'>): ElectricalGroup | null {
  const record = (member.clearances ?? []).find(
    (c) =>
      c.electricalSafetyGroup !== null &&
      (c.category === undefined || c.category === 'ELECTRICAL_SAFETY') &&
      c.status !== 'expired' &&
      c.status !== 'missing',
  );
  return record?.electricalSafetyGroup ?? null;
}

/** Короткое имя допуска в строке статуса — по сути, а не по названию организации. */
function shortName(c: MembershipClearance): string {
  if (c.category === 'MEDICAL') return 'медосмотр';
  if (c.category === 'BRIEFING' && c.kind === 'PERIODIC') return 'повторный инструктаж';
  if (c.category === 'ELECTRICAL_SAFETY') return 'группа по электробезопасности';
  return c.name.toLocaleLowerCase('ru');
}

/** Разовый инструктаж: вводный, первичный. В строке — только если его нет. */
function isOneTimeBriefing(c: MembershipClearance): boolean {
  return c.category === 'BRIEFING' && c.kind === 'ONE_TIME';
}

/**
 * Строка статуса работника относительно вида работ. `null` — сервер итог не
 * прислал (прежняя сборка): говорить о работнике нечего, «готов» без расчёта
 * был бы выдумкой.
 */
export function crewStatus(member: SubdivisionMember, requiredGroup: ElectricalGroup | null): CrewStatus | null {
  if (member.overallStatus === undefined) return null;
  const clearances = member.clearances ?? [];
  const group = crewGroup(member);
  const problems: string[] = [];

  const groupShort =
    requiredGroup !== null && (group === null || GROUP_ORDER.indexOf(group) < GROUP_ORDER.indexOf(requiredGroup));
  if (groupShort) {
    problems.push(
      group === null ? `нет группы по электробезопасности, нужна ${requiredGroup}` : `группа ${group}, нужна ${requiredGroup}`,
    );
  }
  // Просроченное и отсутствующее — в любом допуске, в том числе разовом:
  // вводного или первичного нет вовсе — работник не допущен, и это названо.
  for (const c of clearances) {
    // Группу уже сравнили с требованием; её запись отдельной причиной не
    // повторяем, если группа и так названа.
    if (c.category === 'ELECTRICAL_SAFETY' && groupShort) continue;
    if (c.status === 'missing') problems.push(`${shortName(c)}: нет записи`);
    // Issue #520: запись есть, срок действует, а номера группы в ней нет —
    // решил сервер, и без этой строки отказ по такому работнику назывался бы
    // общим «основания допуска не подтверждены».
    else if (c.electricalGroupMissing) problems.push(`${shortName(c)}: не указан номер группы`);
    else if (c.status === 'expired') {
      problems.push(c.expiresAt ? `${shortName(c)} истёк ${formatDate(c.expiresAt)}` : `${shortName(c)} просрочен`);
    }
  }
  // Сервер считает работника не допущенным, а причины в допусках не видно —
  // так бывает, например, у неподтверждённых записей. Молчать нельзя.
  if (member.overallStatus === 'not_admitted' && problems.length === 0) problems.push('основания допуска не подтверждены');

  if (problems.length > 0) return { tone: 'danger', text: `Не допущен: ${problems.join('; ')}`, problems };

  const ready = group ? `Готов · группа ${group}` : 'Готов';
  const expiring = clearances
    .filter((c) => c.status === 'expiring_soon' && !isOneTimeBriefing(c))
    .map((c) => (c.expiresAt ? `${shortName(c)} истекает ${formatDate(c.expiresAt)}` : `${shortName(c)} истекает`));
  if (expiring.length > 0) return { tone: 'warn', text: [ready, ...expiring].join('; '), problems: [] };
  return { tone: 'ok', text: ready, problems: [] };
}

/**
 * Подробности по нажатию: все допуски работника, включая вводный и первичный,
 * — теми же словами, что клетки «Персонала». Сначала то, по чему решают
 * (медосмотр, повторный инструктаж, группа), затем остальное.
 */
export function crewDetails(member: SubdivisionMember): { name: string; value: string }[] {
  const clearances = member.clearances ?? [];
  const decisive = (['medical', 'repeatBriefing', 'electrical'] as const)
    .map((key) => decisionClearance({ clearances }, key))
    .filter((c): c is MembershipClearance => c !== undefined);
  const rest = clearances.filter((c) => !decisive.includes(c));
  return [...decisive, ...rest].map((c) => ({ name: c.name, value: clearanceCell(c) }));
}

/** Поиск по фамилии и табельному номеру — те же правила, что в «Персонале». */
export function filterCrew<T extends Pick<SubdivisionMember, 'name' | 'employeeNumber'>>(
  people: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLocaleLowerCase('ru');
  if (q.length === 0) return [...people];
  return people.filter(
    (m) => m.name.toLocaleLowerCase('ru').includes(q) || m.employeeNumber.toLocaleLowerCase('ru').includes(q),
  );
}

/**
 * Кто из выбранных в бригаду мешает выписке — до нажатия «Выписать наряд».
 *
 * Подсказка, а не ворота: кнопка не запирается. Решает сервер, и если панель
 * и сервер однажды разойдутся, запертая кнопка не дала бы выписать законный
 * наряд. Отказ сервера всё равно назовёт всех поимённо.
 */
export function blockingCrew(
  people: readonly SubdivisionMember[],
  selectedIds: readonly string[],
  requiredGroup: ElectricalGroup | null,
): { member: SubdivisionMember; reasons: string }[] {
  return people
    .filter((m) => selectedIds.includes(m.id))
    .map((member) => ({ member, status: crewStatus(member, requiredGroup) }))
    .filter((x) => x.status?.tone === 'danger')
    .map((x) => ({ member: x.member, reasons: x.status!.problems.join('; ') }));
}

/**
 * Отказ сервера «Бригада не готова» — поимённо, чтобы подсветить людей в
 * списке.
 *
 * Причины берутся из текста самого отказа, а не собираются заново: сервер
 * пишет строку на работника («ФИО — причины») в том же порядке, что перечень
 * `crew.members`. Второй сборщик причин в панели разошёлся бы с серверным.
 * Не сошлось число строк — причина у работника не называется, но сам он
 * подсвечивается: отказ по нему есть.
 */
export function refusalByWorker(
  message: string,
  crew: { members: readonly { workerId: string }[] } | null | undefined,
): Map<string, string | null> {
  const byWorker = new Map<string, string | null>();
  if (!crew) return byWorker;
  const lines = message.split('\n').slice(1);
  const aligned = lines.length === crew.members.length;
  crew.members.forEach((m, i) => {
    const line = aligned ? lines[i] : undefined;
    const dash = line ? line.indexOf(' — ') : -1;
    byWorker.set(m.workerId, line && dash >= 0 ? line.slice(dash + 3) : null);
  });
  return byWorker;
}
